import {useCallback, useEffect, useRef, useState} from 'react';
import {useLocation} from 'react-router-dom';

import {liveAccessService} from '@/services/liveAccessService';
import type {AfriexQueryParams, BusinessLiveAccessListItem} from '@/types';
import {BusinessLiveAccessStatus} from '@/types';
import {logRejection} from '@/utils/logRejection';

const DEFAULT_STATUS = BusinessLiveAccessStatus.Requested;

export const useLiveAccessList = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const initialStatus =
    (queryParams.get('liveAccessTab') as BusinessLiveAccessStatus) ??
    DEFAULT_STATUS;

  const [list, setList] = useState<BusinessLiveAccessListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  // Set when the last load failed, so the screen can tell a failure apart from
  // a genuinely empty result. Cleared as soon as the next load starts.
  const [loadFailed, setLoadFailed] = useState(false);
  const [isPageReset, setIsPageReset] = useState(false);
  const [displayPage, setDisplayPage] = useState(1);
  const latestRequestRef = useRef(0);
  // Query of the request in flight, so an identical one is not sent twice.
  const inFlightKeyRef = useRef<string | null>(null);

  // The active filter set holds EITHER a status (tab mode) or a searchTerm
  // (search-across-all mode) — never both, mirroring Business Invites.
  const [currentFilters, setCurrentFilters] = useState<AfriexQueryParams>({
    status: initialStatus,
  });
  const [searchTerm, setSearchTerm] = useState<string>('');
  const lastTabStatusRef = useRef<BusinessLiveAccessStatus>(initialStatus);

  const fetchList = useCallback(
    async (params?: AfriexQueryParams) => {
      if (params?.page !== undefined) {
        setDisplayPage(params.page + 1);
      }
      const query = {
        page: params?.page ?? 0,
        limit: params?.limit ?? 10,
        status: currentFilters.status as BusinessLiveAccessStatus | undefined,
        search: currentFilters.searchTerm,
      };
      // Pagination fetches page 0 when it mounts or resets, in the same
      // commit as the filter/tab effect below; skip whichever arrives second.
      const key = JSON.stringify(query);
      if (key === inFlightKeyRef.current) {
        return;
      }
      inFlightKeyRef.current = key;
      const requestId = ++latestRequestRef.current;
      try {
        setIsLoading(true);
        setLoadFailed(false);
        let data;
        try {
          data = await liveAccessService.getList(query);
        } finally {
          if (inFlightKeyRef.current === key) {
            inFlightKeyRef.current = null;
          }
        }
        // A newer load was requested while this one was in flight: that one
        // owns the list now, so drop this response instead of reverting.
        if (requestId !== latestRequestRef.current) {
          return;
        }
        setList(data.data ?? []);
        setTotal(data.total ?? 0);
      } catch (_) {
        if (requestId === latestRequestRef.current) {
          setList([]);
          setTotal(0);
          setLoadFailed(true);
        }
      } finally {
        if (requestId === latestRequestRef.current) {
          setIsLoading(false);
        }
      }
    },
    [currentFilters],
  );

  useEffect(() => {
    // `fetchList` catches into an empty list, so this only fires if that catch
    // or the finally itself throws. Log rather than swallow silently.
    fetchList({page: 0, limit: 10}).catch(
      logRejection('Failed to load live access list'),
    );
  }, [fetchList]);

  // Called by the tab change: swap to a status view and clear any search.
  const setTabStatus = useCallback((nextStatus: BusinessLiveAccessStatus) => {
    lastTabStatusRef.current = nextStatus;
    setSearchTerm('');
    setList([]);
    setTotal(0);
    setCurrentFilters({status: nextStatus});
    setIsPageReset((prev) => !prev);
  }, []);

  // Search runs across all statuses (status filter dropped); clearing restores
  // the last active tab.
  const handleSearch = useCallback(
    (search: string) => {
      const trimmed = search.trim();
      // An empty search with no search active changes nothing, as in
      // clearFilters below, so it must not refetch.
      if (
        !trimmed &&
        !currentFilters.searchTerm &&
        currentFilters.status === lastTabStatusRef.current
      ) {
        return;
      }
      setSearchTerm(trimmed);
      setCurrentFilters(
        trimmed ? {searchTerm: trimmed} : {status: lastTabStatusRef.current},
      );
      setIsPageReset((prev) => !prev);
    },
    [currentFilters],
  );

  // The SearchBar calls this on mount while its box is empty. With no search
  // to clear, a new filter object would read as a filter change and refetch
  // the list, so leave the filters as they are.
  const clearFilters = useCallback(() => {
    if (
      !currentFilters.searchTerm &&
      currentFilters.status === lastTabStatusRef.current
    ) {
      return;
    }
    setSearchTerm('');
    setCurrentFilters({status: lastTabStatusRef.current});
    setIsPageReset((prev) => !prev);
  }, [currentFilters]);

  return {
    list,
    total,
    isLoading,
    loadFailed,
    isPageReset,
    displayPage,
    isSearchActive: searchTerm.length > 0,
    fetchList,
    setTabStatus,
    handleSearch,
    clearFilters,
  };
};
